import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { Search, Heart, Download, Filter, Eye } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Helper to build viewer URL
function getViewerUrl(fileUrl, title) {
  return `/notes/view?file=${encodeURIComponent(fileUrl)}&title=${encodeURIComponent(title || 'Notes')}`;
}

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const branches = ["All", "AIML", "BT", "CHE", "CV", "CSE", "DS", "ECE", "EEE", "EIE", "IEM", "ISE", "ME"];
const semesters = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const types = ["All", "notes", "pyq", "syllabus", "lab_manual"];

// Helper to format type for display
function formatType(type) {
  if (type === "pyq") return "PYQ";
  if (type === "lab_manual") return "Lab Manual";
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export default function ResourcesPage() {
  const [searchParams] = useSearchParams();
  const [resources, setResources] = useState([]);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [branch, setBranch] = useState("");
  const [semester, setSemester] = useState(0);
  const [resourceType, setResourceType] = useState(searchParams.get("resource_type") || "");

  const fetchResources = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (branch && branch !== "All") params.set("branch", branch);
    if (semester > 0) params.set("semester", semester);
    if (resourceType && resourceType !== "All") params.set("resource_type", resourceType);
    axios
      .get(`${API}/resources?${params.toString()}`)
      .then((r) => setResources(r.data))
      .catch(() => {});
  }, [search, branch, semester, resourceType]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  const handleLike = async (id) => {
    toast.info("Liked!");
  };

  const handleDownload = async (id, fileUrl, title) => {
    try {
      await axios.post(`${API}/resources/${id}/download`);
      setResources((prev) =>
        prev.map((r) => (r.id === id ? { ...r, downloads: r.downloads + 1 } : r))
      );
      if (fileUrl) {
        // Handle both local and external (Cloudinary) URLs
        const downloadUrl = fileUrl.startsWith('http') ? fileUrl : `${BACKEND}${fileUrl}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = title || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.success("Download tracked");
      }
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div data-testid="resources-page" className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeInUp} className="mb-10">
            <span className="text-xs font-medium tracking-widest text-emerald-400 uppercase mb-2 block">Study Materials</span>
            <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">Resources</h1>

            {/* Search & Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  data-testid="resource-search-input"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search resources..."
                  className="w-full bg-black/50 border border-white/10 focus:border-violet-500/50 rounded-full pl-11 pr-5 py-3 text-sm text-white placeholder:text-zinc-600 backdrop-blur-xl outline-none focus:ring-2 focus:ring-violet-500/20 transition-shadow"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Select value={branch || "All"} onValueChange={(v) => setBranch(v === "All" ? "" : v)}>
                  <SelectTrigger data-testid="branch-filter" className="w-[120px] bg-black/50 border-white/10 text-white rounded-full text-sm">
                    <Filter className="w-3 h-3 mr-1 text-zinc-500" />
                    <SelectValue placeholder="Branch" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 max-h-60">
                    {branches.map((b) => (
                      <SelectItem key={b} value={b} className="text-zinc-300">{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={String(semester)} onValueChange={(v) => setSemester(Number(v))}>
                  <SelectTrigger data-testid="semester-filter" className="w-[120px] bg-black/50 border-white/10 text-white rounded-full text-sm">
                    <SelectValue placeholder="Semester" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {semesters.map((s) => (
                      <SelectItem key={s} value={String(s)} className="text-zinc-300">{s === 0 ? "All Sems" : `Sem ${s}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={resourceType || "All"} onValueChange={(v) => setResourceType(v === "All" ? "" : v)}>
                  <SelectTrigger data-testid="type-filter" className="w-[150px] bg-black/50 border-white/10 text-white rounded-full text-sm">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {types.map((t) => (
                      <SelectItem key={t} value={t} className="text-zinc-300">{t === "All" ? "All Types" : formatType(t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {resources.map((res) => (
              <motion.div key={res.id} variants={fadeInUp}>
                <div
                  data-testid={`resource-item-${res.id}`}
                  className="group rounded-2xl bg-white/[0.03] border border-white/10 p-6 hover:border-white/20 hover:-translate-y-1"
                  style={{ transitionProperty: "border-color, transform", transitionDuration: "300ms" }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-violet-600/10 text-violet-300 border border-violet-500/20">
                      {formatType(res.resource_type)}
                    </span>
                    <span className="text-xs text-zinc-600">Sem {res.semester}</span>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-white mb-1 line-clamp-2">{res.title}</h3>
                  <p className="text-xs text-zinc-500 mb-4">{res.branch} • {res.subject}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        data-testid={`like-btn-${res.id}`}
                        onClick={() => handleLike(res.id)}
                        className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-pink-400 transition-colors"
                      >
                        <Heart className="w-4 h-4" />
                        {res.likes}
                      </button>
                      <button
                        data-testid={`download-btn-${res.id}`}
                        onClick={() => handleDownload(res.id, res.file_url, res.title)}
                        className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-cyan-400 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        {res.downloads}
                      </button>
                      {res.file_url && (
                        <a 
                          href={getViewerUrl(res.file_url, res.title)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-emerald-400 transition-colors"
                        >
                          <Eye className="w-4 h-4" /> View
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {resources.length === 0 && (
            <div className="text-center py-20 text-zinc-500">No resources found. Try adjusting your filters.</div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
